import {
  ChangeDetectionStrategy,
  Component,
  input,
  output,
} from '@angular/core';

/**
 * Variantes visuales del botón.
 * - `white`: fondo blanco, texto negro y borde gris; hover en azul.
 * - `danger`: acciones destructivas (Eliminar, vaciar, etc.).
 * - `icon`: botón de ícono cuadrado redondeado o circular; la forma se define con `iconShape`.
 * - `document`: botón cuadrado de fondo blanco y bordes redondeados; el archivo a color lo define `iconSrc`.
 */
export type UiButtonVariant =
  | 'primary'
  | 'secondary'
  | 'white'
  | 'ghost'
  | 'danger'
  | 'icon'
  | 'document';
export type UiButtonSize = 'sm' | 'md' | 'lg';
export type UiButtonIconBackground = 'blue' | 'white';
export type UiButtonIconShape = 'square' | 'circle';

/**
 * Átomo: botón presentacional. Sin lógica de negocio ni HTTP.
 */
@Component({
  selector: 'ui-button',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [],
  templateUrl: './button.component.html',
  styleUrl: './button.component.scss',
  host: {
    class: 'ui-button-host',
    '[class.ui-button-host--block]': 'block()',
  },
})
export class UiButtonComponent {
  /**
   * Variante visual.
   * Usar `danger` para acciones que eliminan o no se pueden deshacer con facilidad.
   * Usar `icon` para un botón cuadrado redondeado o circular, solo con ícono.
   * Usar `document` para un archivo a color sobre un botón cuadrado de fondo blanco.
   */
  readonly variant = input<UiButtonVariant>('primary');

  /** Fondo de `variant="icon"`. No aplica a las demás variantes. */
  readonly iconBackground = input<UiButtonIconBackground>('blue');

  /** Forma de `variant="icon"`. Por defecto conserva el cuadrado redondeado. */
  readonly iconShape = input<UiButtonIconShape>('square');

  /**
   * Ruta del ícono (p. ej. `/icons/ic_mail.svg`).
   * En `icon` es el único contenido. En el resto, va a la izquierda del texto.
   */
  readonly iconSrc = input<string | undefined>(undefined);

  /** Tamaño */
  readonly size = input<UiButtonSize>('md');

  /** Etiqueta accesible. Obligatoria en la práctica cuando `variant="icon"`. */
  readonly ariaLabel = input<string | undefined>(undefined);

  /** Estado expandido para botones que controlan menús o paneles. */
  readonly ariaExpanded = input<boolean | undefined>(undefined);

  /** Deshabilitado */
  readonly disabled = input(false);

  /** Muestra estado de carga (deshabilita el click) */
  readonly loading = input(false);

  /** Tipo nativo del botón */
  readonly type = input<'button' | 'submit' | 'reset'>('button');

  /** Ocupa todo el ancho disponible */
  readonly block = input(false);

  /** Texto accesible cuando loading=true */
  readonly loadingLabel = input('Cargando');

  /** Click (no emite si disabled o loading) */
  readonly pressed = output<MouseEvent>();

  protected onClick(event: MouseEvent): void {
    if (this.disabled() || this.loading()) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    this.pressed.emit(event);
  }
}
